"use client";

import { useEffect, useRef, useState } from "react";
import { authClient } from "@/lib/auth/auth-client";
import { getCurrentAreaInClient } from "@/lib/utils.client";

/**
 * 全局地区变化检测和登出处理
 * 当检测到地区变化时，使用 authClient.signOut() 进行正确的登出
 */
export function useGlobalRegionChange() {
  const isProcessingRef = useRef(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  useEffect(() => {
    const checkRegionChange = async () => {
      const currentArea = getCurrentAreaInClient();
      const lastArea = localStorage.getItem("lastRegion");

      // 如果没有上次记录，直接保存当前地区
      if (!lastArea) {
        localStorage.setItem("lastRegion", currentArea);
        return;
      }

      // 检查地区是否变化
      if (lastArea !== currentArea && !isProcessingRef.current) {
        // 从服务器获取有效的地区列表，而不是硬编码
        const validAreas = ["HK", "MO", "TW", "GB"];
        if (validAreas.includes(currentArea) && validAreas.includes(lastArea)) {
          isProcessingRef.current = true;
          setIsRedirecting(true);

          // 立即更新记录防止重复触发
          localStorage.setItem("lastRegion", currentArea);
          try {
            // 检查是否已登录，如果是则先登出
            const session_0 = await authClient.getSession();
            if (session_0?.data?.user) {
              await authClient.signOut();
              // 已登录用户：重定向到新地区的登录页
              const loginUrl = `/${currentArea.toLowerCase()}/user/login`;
              window.location.href = loginUrl;
            } else {
              // 未登录用户：保持当前页面，只更新URL中的地区部分
              const currentPath_1 = window.location.pathname;
              // 处理双斜杠等异常路径，确保路径格式正确
              const cleanPath_1 = currentPath_1.replace(/\/+/g, "/");
              const newPath_1 = cleanPath_1.replace(/^\/[^/]*/, `/${currentArea.toLowerCase()}`);
              window.location.href = newPath_1;
            }
          } catch {
            // 出错时根据当前登录状态决定重定向目标
            try {
              const session = await authClient.getSession();
              if (session?.data?.user) {
                // 已登录用户出错时重定向到登录页
                window.location.href = `/${currentArea.toLowerCase()}/user/login`;
              } else {
                // 未登录用户出错时保持当前页面
                const currentPath_0 = window.location.pathname;
                // 处理双斜杠等异常路径，确保路径格式正确
                const cleanPath_0 = currentPath_0.replace(/\/+/g, "/");
                const newPath_0 = cleanPath_0.replace(/^\/[^/]*/, `/${currentArea.toLowerCase()}`);
                window.location.href = newPath_0;
              }
            } catch {
              // 完全出错时保持当前页面
              const currentPath = window.location.pathname;
              // 处理双斜杠等异常路径，确保路径格式正确
              const cleanPath = currentPath.replace(/\/+/g, "/");
              const newPath = cleanPath.replace(/^\/[^/]*/, `/${currentArea.toLowerCase()}`);
              window.location.href = newPath;
            }
          }
        }
      }
    };

    // 立即检查一次
    checkRegionChange();

    // 监听 URL 变化
    const handleUrlChange = () => {
      setTimeout(checkRegionChange, 100);
    };
    window.addEventListener("popstate", handleUrlChange);
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      handleUrlChange();
    };
    history.replaceState = (...args_0) => {
      originalReplaceState.apply(history, args_0);
      handleUrlChange();
    };
    return () => {
      window.removeEventListener("popstate", handleUrlChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);
  return {
    isRedirecting
  };
}